import React, { ReactElement, MouseEvent } from 'react';
import _, { snakeCase } from 'lodash';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-v5';
import { IAppState } from 'spw-redux/reducer';
import { DragDropContext, Draggable, DropResult } from 'react-beautiful-dnd';
import { StrictModeDroppable } from 'spw-components/global/strict-mode-droppable';
import {
  setIsLoading as setIsLoadingAction,
  saveWorksheet as saveWorksheetAction,
  fetchCurrentWorksheet as fetchCurrentWorksheetAction,
  populateWorksheet as populateWorksheetAction,
  postWorksheetRow as postWorksheetRowAction,
  updateChildWorksheetSpaceType as updateChildWorksheetSpaceTypeAction,
  populateWorksheetFromRequest as populateWorksheetFromRequestAction,
} from 'spw-redux/modules/calculators/actions';
import { actions as customSpaceActions } from 'spw-redux/modules/custom_spaces/actions';
import {
  fetchScenariosAssociatedWithSelectedRow as fetchScenariosAssociatedWithSelectedRowAction,
  setSelectedWorksheetRow as setSelectedWorksheetRowAction,
  setSelectedSpaceType as setSelectedSpaceTypeAction,
  setSelectedChildSpaceType as setSelectedChildSpaceTypeAction,
  setRemoveSpaceTypeDialogStatus as setRemoveSpaceTypeDialogStatusAction,
  setIsSpaceTypeDeletable as setIsSpaceTypeDeletableAction,
  setWorksheetDecimalsAreEnabled as setWorksheetDecimalsAreEnabledAction,
} from 'spw-redux/modules/worksheet/actions';
import {
  getSpaceTypeCategories as getSpaceTypeCategoriesAction,
  clearSpaceCategories as clearSpaceCategoriesAction,
} from 'spw-redux/modules/projects/actions';
import {
  IWorksheet,
  IWorksheetRow,
  IChildWorksheetSpaceType,
  ICategoryNote,
  WorksheetCategory,
} from 'spw-redux/modules/calculators/types';
import {
  WorksheetDefaultCategory,
  CanDeleteSpaceTypeEnum,
} from 'spw-redux/modules/worksheet/types';
import { always, cond, equals, groupBy, isEmpty, isNil, pathOr } from 'ramda';
import {
  CUSTOM_SUBCATEGORY,
  GENERAL_CATEGORY_TITLE,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  MACRO_PROGRAMMING_TYPE,
  NOTE_GENERAL_CATEGORY,
  PRIVATE_OFFICES_SUBCATEGORY,
  SHARED_SPACES_CATEGORY,
  SPACE_NAME_MAP,
  SUPPORT_SPACES_CATEGORY,
  TITLES,
  TOTAL_PROGRAM_TYPE,
  WORKSTATIONS_SUBCATEGORY,
} from 'spw-constants';
import { bindActionCreators, Dispatch } from 'redux';
import { CenteredColumn } from 'spw-shared/globalComponents/programSummary/styles';
import { CircularProgress, Grid } from '@mui/material';
import {
  IBuilding,
  IDepartment,
  IFloor,
  IProject,
  IProjectColors,
  IProjectSpaceTypeCategory,
  IProjectSpaceTypeSubcategory,
} from 'spw-redux/modules/projects/types';
import { IUser } from 'spw-redux/modules/user/types';
import {
  ISpaceTypeCategory,
  ISpaceType,
  IProjectSpaceType,
} from 'spw-redux/modules/space_types/types';

import { isTotalProgram } from 'spw-components/programming-scenarios/building-hs-sc-scenario/utils';
import { ScenarioHeader } from 'spw-components/downloads/image-header';
import { IProjectSettings } from 'spw-redux/modules/settings/types';

import {
  actions as spaceTypesActions,
  toggleClientFacingNamesAreVisible,
  updateProjectSpaceType as updateProjectSpaceTypeAction,
} from 'spw-redux/modules/space_types/actions';
import {
  TotalProgramWorksheetNotes,
  WorksheetNote,
} from 'spw-programming-projects/program-summary/program-summary-tabs/summary-notes';
import { selectCurrentUserIsViewOnly } from 'spw-redux/modules/projects/selectors';
import WorksheetNotes from 'spw-components/dialogs/worksheet/worksheet-notes';
import { getWorksheetsByScenarioIdMap } from 'spw-components/programming-scenarios/utils';
import SpaceTypeCategoryRow from 'spw-components/worksheet/space-type-category-row';
import {
  selectProgramSummaryCalculator,
  selectTpsWorksheets,
} from 'spw-redux/modules/calculators/selectors';
import SubcategoryRows from 'spw-programming-projects/building-details/worksheet/subcategory-rows';
import { WorksheetContext } from 'spw-programming-projects/building-details/worksheet/worksheet-context';
import SpaceTypeRow from 'spw-programming-projects/building-details/worksheet/space-type-row';
import { isIWCategoryOrSubcategory } from 'spw-projects/utils';
import { selectWorksheetDecimalsAreEnabled } from 'spw-redux/modules/worksheet/selectors';
import { getColor } from 'spw-shared/utils';
import ManageSpaceTypesPopover from 'spw-programming-projects/building-details/worksheet/manage-space-types-popover';
import WorksheetContainer from './worksheet-container';
import WorksheetHeader from './worksheet-header';
import ProgrammingCategoryHeader from './programming-category-header';
import WorkSeatSubTotalRow from './work-seat-subtotal-row';

import {
  DEFAULT_INDIVIDUAL_NAME,
  isDuplicateSpace,
  isPreNamedSpace,
  generateSortingAfterDrag,
  shouldDisplayAsRatio,
} from './utils';
import SharedSpacesRow from './macro-shared-spaces-row';
import MacroProgrammingCategoryHeader from './macro-programming-category-header';
import StorageCategoryHeader from './storage-category-header';
import { getItemStyle, getListStyle } from './styles';
import Worksheet, { PlainWorksheet } from '../../models/Worksheet';
import { PlainProgramScenario } from '../../models/Scenario';
import { SummaryCalculator } from '../../models/ProgramSummaryCalculator';
import WorksheetRow from '../../models/WorksheetRow';
import ParentSpaceTypeRow from '../../projects/programming/building-details/worksheet/parent-space-type-row';
import StorageSpaceTypeRow from '../../projects/programming/building-details/worksheet/storage-space-type-row';
import WorksheetStorageSpaceRow from '../../models/WorksheetStorageSpaceRow';
import ParentStorageSpaceTypeRow from '../../projects/programming/building-details/worksheet/parent-storage-space-type-row';

interface IWorksheetProps {
  project: IProject;
  allCategories: ISpaceTypeCategory[];
  projectSpaceTypes: IProjectSpaceType[];
  childWorksheetSpaceTypes: IChildWorksheetSpaceType[];
  scenario?: PlainProgramScenario;
  worksheet: PlainWorksheet;
  programSummaryCalculator: SummaryCalculator;
  tpsWorksheetsEnhanced?: PlainWorksheet[];
  inBrowserDownload?: boolean;
  getSpaceTypeCategories: () => void;
  setIsLoading: (isLoading: boolean) => void;
  fetchCurrentWorksheet: () => void;

  saveWorksheet: () => void;
  isLoading: boolean;
  populateWorksheet: (worksheet: PlainWorksheet) => void;
  building: IBuilding;
  setCurrentParent: (parent: ISpaceType) => void;
  postWorksheetRow: (
    worksheetRow: IWorksheetRow,
    worksheetId: number,
    isNew?: boolean,
    unitsHigh?: number,
    name?: string
  ) => void;
  clearSpaceCategories: () => void;
  downloadParams?: {
    category?: string;
  };
  user?: IUser;
  isEditingSavedScenario?: boolean;
  createCustomSpaceType: ({
    spaceType,
    insertToWorksheet,
    previousId,
  }: {
    spaceType: ISpaceType;
    insertToWorksheet?: boolean;
    previousId: number | null;
  }) => void;
  updateCustomSpaceType: (id: number, { name }: { name: string }) => void;
  updateProjectSpaceType: (args: {
    categoryId?: number;
    projectSpaceTypeId: number;
    spaceTypeId?: number;
    icon?: string | null;
    clientFacingName?: string;
  }) => void;
  isWorksheetOpenForDownload?: boolean;
  projectColors: IProjectColors;
  updateChildWorksheetSpaceType: (
    childWorksheetSpaceType: Partial<IChildWorksheetSpaceType>,
    unitsHigh: number | null
  ) => void;
  showWorksheetNotesDialog: boolean;
  projectSettings: IProjectSettings;
  bulkUpdateProjectSpaceTypes: (pst: IProjectSpaceType[]) => void;
  setIsSpaceTypeDeletable: (canDeleteSpaceType: boolean) => void;
  fetchScenariosAssociatedWithSelectedRow: (
    projectId: number,
    spaceTypeId: number,
    isChild: boolean
  ) => void;
  setSelectedWorksheetRow: (row: WorksheetRow) => void;
  setSelectedSpaceType: (spaceType?: IProjectSpaceType) => void;
  setSelectedChildSpaceType: (
    childSpaceType?: IChildWorksheetSpaceType
  ) => void;
  setRemoveSpaceTypeDialogStatus: (
    status: boolean,
    hideAllMode?: boolean
  ) => void;
  canDeleteSpaceType: CanDeleteSpaceTypeEnum;
  populateWorksheetFromRequest: (worksheet: IWorksheet) => void;
  isShowingWorksheetUpdateDialog: boolean;
  isRenderingForExport?: boolean;
  currentUserIsViewOnly: boolean;
  toggleClientFacingNames: () => void;
  clientFacingNamesAreVisible: boolean;
  setWorksheetDecimalsAreEnabled: (enabled: boolean) => void;
  decimalsAreEnabled: boolean;
}

type WorksheetProps = IWorksheetProps & RouteComponentProps;

interface WorksheetState {
  category?: WorksheetDefaultCategory | null;
  manageSpaceTypesDialogAnchorEl?: HTMLElement;
  selectedSpaceTypeCategoryId?: number;
  collapsed: { [id: string]: boolean };
  didSetInitialCollapsedState: boolean;
  activeRowIdentifier: string | null;
  activeRow: WorksheetRow;
  newRowId: null | string;
  userIsEditingASpaceName: boolean;
}

export class Programming extends React.Component<
  WorksheetProps,
  WorksheetState
> {
  newRowRef: React.RefObject<HTMLDivElement>;

  refMap: Record<string, React.RefObject<HTMLDivElement>>;

  constructor(props: WorksheetProps) {
    super(props);
    const collapsed = {};

    this.state = {
      collapsed,
      didSetInitialCollapsedState: false,
      manageSpaceTypesDialogAnchorEl: null,
      activeRow: null,
      activeRowIdentifier: null,
      newRowId: null,
      userIsEditingASpaceName: false,
      selectedSpaceTypeCategoryId: null,
    };

    this.renameSpaceType = this.renameSpaceType.bind(this);
    this.onManageSpaceTypesButtonClick = this.onManageSpaceTypesButtonClick.bind(
      this
    );
    this.onCloseManageSpaceTypesDialog = this.onCloseManageSpaceTypesDialog.bind(
      this
    );
    this.changeChildRow = this.changeChildRow.bind(this);
    this.onChangeRow = this.onChangeRow.bind(this);
    this.setCollapsedRow = this.setCollapsedRow.bind(this);
    this.handleDragEnd = this.handleDragEnd.bind(this);
    this.showHeader = this.showHeader.bind(this);
    this.executeScroll = this.executeScroll.bind(this);
    this.newRowRef = React.createRef();
    this.refMap =
      props.project.spaceTypeCategories?.reduce(
        (map, projectSpaceTypeCategory: IProjectSpaceTypeCategory) => ({
          ...map,
          [projectSpaceTypeCategory.defaultSpaceTypeCategory
            .category]: React.createRef(),
        }),
        {}
      ) || {};
    this.refMap[SHARED_SPACES_CATEGORY] = React.createRef();
  }

  componentDidMount() {
    const { populateWorksheet, fetchCurrentWorksheet } = this.props;
    document.addEventListener('keydown', this.handleTabPressed);

    populateWorksheet(null);
    fetchCurrentWorksheet();
  }

  componentDidUpdate(prevProps: WorksheetProps) {
    const {
      allCategories,
      worksheet,
      isRenderingForExport,
      isWorksheetOpenForDownload,
    } = this.props;
    const { newRowId: stateId } = this.state;

    // the first condition filters out users who have logged in and loaded the component for the first time
    // the second condition prevProps.worksheet.id == worksheet.id filters out users who are switching worksheets
    // the third condition matches the condition where a new row was just added to the worksheet
    if (
      prevProps?.worksheet?.id === worksheet?.id &&
      prevProps?.worksheet?.rows &&
      worksheet?.rows &&
      !_.isEqual(worksheet?.rows, prevProps?.worksheet?.rows)
    ) {
      // find the new row
      let res: WorksheetRow;
      const worksheetRowMap: Record<string, WorksheetRow> = {};
      for (const i of prevProps.worksheet?.rows) {
        if (i?.id) {
          worksheetRowMap[i?.id] = i;
        }
      }
      for (const j of worksheet?.rows) {
        if (j?.id) {
          if (
            !worksheetRowMap[j?.id] ||
            (!!worksheetRowMap[j?.id]?.removed !== !!j.removed && !j.removed)
          ) {
            res = j;
            break;
          }
        }
      }

      let newRowId = null;
      if (res) {
        newRowId = `${res?.projectSpaceType?.id}-${!!res?.child}`;
      }
      if (newRowId !== stateId && newRowId) {
        // wait until setState has completed to commence the scrolling action
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ newRowId }, () =>
          res ? this.scrollToNewRow() : Function.prototype
        );
      }
    }

    if (isEmpty(allCategories) || isNil(allCategories)) {
      return;
    }

    const { collapsed, didSetInitialCollapsedState } = this.state;

    if (worksheet && worksheet.rows && !didSetInitialCollapsedState) {
      // currently, as componentDidReceiveProps is being deprecated, there is no good alternative
      // to setting state in componentDidUpdate. The reason for react/no-did-update-set-state to is
      // to ensure the code does not enter an infinite loop. We are protecting against the infinite loop
      // by setting didSetInitialCollapsedState and checking for that, so in this case we are protected
      // from the infinite loop.

      // more info: https://github.com/yannickcr/eslint-plugin-react/issues/1707
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        didSetInitialCollapsedState: true,
        collapsed: {
          ...collapsed,
          ...worksheet.rows?.reduce((accumulator, row: WorksheetRow) => {
            if (
              isRenderingForExport &&
              isWorksheetOpenForDownload !== undefined
            ) {
              accumulator[
                this.getCollapsedId(row)
              ] = !isWorksheetOpenForDownload;
              return accumulator;
            }

            const hasPerDepartmentRows = !!worksheet.breakDownRows[
              row.uniqueKey
            ];
            const shouldBeInitiallyCollapsed =
              hasPerDepartmentRows ||
              (isRenderingForExport &&
                row.category === 'supportSpaces' &&
                !row.child);
            accumulator[this.getCollapsedId(row)] = shouldBeInitiallyCollapsed;
            return accumulator;
          }, {} as { [id: string]: boolean }),
        },
      });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleTabPressed);
    const { populateWorksheet } = this.props;
    populateWorksheet(null);
  }

  handleTabPressed = (e: KeyboardEvent) => {
    const { scenario } = this.props;
    if (e.key === 'Tab' && isTotalProgram(scenario)) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  handleDragEnd = (
    result: DropResult,
    spaceTypeCategory: IProjectSpaceTypeCategory
  ) => {
    const { scenario } = this.props;
    const { userIsEditingASpaceName } = this.state;
    // don't allow drag on total program, doesn't really make sense with everything being disabled
    if (isTotalProgram(scenario) || userIsEditingASpaceName) return;

    const { worksheet, bulkUpdateProjectSpaceTypes } = this.props;

    // check to make sure that the row was dragged to the correct category
    if (
      result?.destination &&
      result?.destination?.index !== undefined &&
      result.draggableId
    ) {
      const apiWorksheet = Worksheet.toAPIWorksheet(worksheet);
      const reorderedRows = apiWorksheet.rows.slice();
      const firstCategory = _.findIndex(
        reorderedRows,
        (r) => r.category.id === spaceTypeCategory.id
      );
      const categorySpaceRows = reorderedRows.filter(
        (r) => r.category.id === spaceTypeCategory.id
      );

      const {
        newOrderedRows,
        projectSpaceTypesToUpdate,
      } = generateSortingAfterDrag({
        rowProjectSpaceTypeId: parseInt(result.draggableId, 10),
        newIndex: result.destination.index,
        categorySpaceRows,
      });

      reorderedRows.splice(
        firstCategory,
        newOrderedRows.length,
        ...newOrderedRows
      );

      this.updateWorksheet(
        new Worksheet(
          { ...apiWorksheet, rows: reorderedRows },
          worksheet.scenario,
          worksheet.projectSettings
        ).toPlainObject()
      );

      bulkUpdateProjectSpaceTypes(
        projectSpaceTypesToUpdate as IProjectSpaceType[]
      );
    }
  };

  handleRowMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (isNil((event.target as HTMLElement).closest('.MuiPopper-root'))) {
      event.currentTarget.focus();
    }
  };

  handleRowKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.currentTarget.focus();
    }
  };

  onChangeRow(
    row: WorksheetRow,
    options: {
      name?: string;
      isNameUpdate?: boolean;
      isCustomSpaceType?: boolean;
    }
  ) {
    if (options?.isNameUpdate)
      return this.renameSpaceType(
        row,
        options?.name,
        options?.isCustomSpaceType
      );
    const {
      worksheet,
      populateWorksheetFromRequest,
      saveWorksheet,
    } = this.props;

    let { rows } = worksheet;
    rows = rows.filter((r) => !r.removed);
    const rowIndex: number = _.findIndex(rows, (r) => r.id === row.id);

    if (rowIndex > -1) {
      rows.splice(rowIndex, 1, row);
    }
    populateWorksheetFromRequest(
      Worksheet.toAPIWorksheet({
        ...worksheet,
        rows,
      })
    );
    return saveWorksheet();
  }

  onManageSpaceTypesButtonClick(
    event: React.MouseEvent<HTMLDivElement>,
    spaceTypeCategoryId: number
  ) {
    this.setState({
      manageSpaceTypesDialogAnchorEl: event.currentTarget,
      selectedSpaceTypeCategoryId: spaceTypeCategoryId,
    });
  }

  onCloseManageSpaceTypesDialog() {
    this.setState({
      manageSpaceTypesDialogAnchorEl: null,
    });
  }

  getSubcategoryParentSpaceType = (subcategory: string) => {
    return cond([
      [equals(PRIVATE_OFFICES_SUBCATEGORY), always('custom_office_type')],
      [equals(WORKSTATIONS_SUBCATEGORY), always('custom_workspace_type')],
    ])(subcategory);
  };

  setCollapsedRow(row: WorksheetRow, isTotalProgramParentRow: boolean) {
    this.toggleParentRowCollapsedState(row, {
      isTotalProgramParentRow,
    });
  }

  toggleShowDecimals = () => {
    const { setWorksheetDecimalsAreEnabled, decimalsAreEnabled } = this.props;
    setWorksheetDecimalsAreEnabled(!decimalsAreEnabled);
  };

  scrollToCategory = (category: string) => this.executeScroll(category);

  showHeader = (rows: WorksheetRow[]) => {
    return _(rows)
      .filter((r) => !r.isStorageSupportSpace)
      .some((r) => !!r);
  };

  getCollapsedId = (row: WorksheetRow) =>
    `${row.child ? 'child-' : ''}${row.spaceType?.id}`;

  toggleParentRowCollapsedState = (
    row: WorksheetRow,
    options: { isTotalProgramParentRow: boolean }
  ) => {
    const { worksheet } = this.props;
    const { collapsed } = this.state;
    if (
      options.isTotalProgramParentRow ||
      _.some(worksheet.rows, (child: WorksheetRow) => {
        return (
          child.child && child?.spaceType?.parent?.id === row.spaceType?.id
        );
      })
    ) {
      this.setState({
        collapsed: {
          ...collapsed,
          [this.getCollapsedId(row)]: !collapsed[this.getCollapsedId(row)],
        },
      });
    }
  };

  isActiveRowDirty() {
    const { activeRow } = this.state;
    const { worksheet } = this.props;

    const isRowDirty = (row: WorksheetRow) => {
      const rowFields = [
        'ratio',
        'quantity',
        'sizeX',
        'sizeY',
        'usf',
      ] as (keyof typeof row)[];

      return rowFields.some(
        (rowField) => row[rowField] !== 0 && row[rowField] !== null
      );
    };

    // Parent rows are clean if either:
    //  * they don't have children
    //  * they have them, but the children are clean

    if (!activeRow.child) {
      const isIndividualWorkspaceInDetailedScenario =
        worksheet.scenario.isDetailed &&
        activeRow.category === INDIVIDUAL_WORKSPACES_CATEGORY;
      const isStorage = [
        SPACE_NAME_MAP.SUPPORT_SPACES.STORAGE_LOCKER,
        SPACE_NAME_MAP.SUPPORT_SPACES.FILE_CABINET,
      ].includes(activeRow.spaceType.spaceType);

      if (isIndividualWorkspaceInDetailedScenario || isStorage) {
        const childRows = worksheet.rows.filter((row) => {
          if (!row.child || row.removed) return false;

          return row.spaceType.parent.id === activeRow.spaceTypeId;
        });

        if (!childRows.length) return false;

        return childRows.some(isRowDirty);
      }
    }

    return isRowDirty(activeRow);
  }

  renameSpaceType(
    row: WorksheetRow,
    newName: string,
    isCustomSpaceType?: boolean
  ) {
    if (row.child) {
      this.changeChildRow(row, { name: newName, isNameUpdate: true });
      return true;
    }

    const {
      updateCustomSpaceType,
      projectSpaceTypes,
      childWorksheetSpaceTypes,
      updateProjectSpaceType,
      clientFacingNamesAreVisible,
    } = this.props;

    if (
      !isDuplicateSpace(
        projectSpaceTypes,
        childWorksheetSpaceTypes,
        newName,
        row.spaceType.id
      )
    ) {
      if (
        isCustomSpaceType &&
        !isPreNamedSpace(newName) &&
        !clientFacingNamesAreVisible
      )
        updateCustomSpaceType(row.spaceType.id, { name: newName });
      else {
        updateProjectSpaceType({
          projectSpaceTypeId: row.projectSpaceType.id,
          clientFacingName: newName,
        });
        if (row.name === DEFAULT_INDIVIDUAL_NAME) {
          updateCustomSpaceType(row.spaceType.id, { name: newName });
        }
      }

      return true;
    }

    return false;
  }

  executeScroll(category: string) {
    if (this.refMap[category]) {
      this.refMap[category].current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }

  updateWorksheet(worksheet: PlainWorksheet) {
    const { populateWorksheet } = this.props;
    populateWorksheet(worksheet);
  }

  changeChildRow(
    row: WorksheetRow,
    options: { name?: string; isNameUpdate?: boolean }
  ) {
    const {
      worksheet,
      updateChildWorksheetSpaceType,
      saveWorksheet,
      populateWorksheetFromRequest,
      clientFacingNamesAreVisible,
    } = this.props;

    let { rows } = worksheet;
    rows = rows.filter((r) => !r.removed);

    const rowIndex: number = _.findIndex(rows, (r) => r.id === row.id);
    if (rowIndex > -1) {
      rows.splice(rowIndex, 1, row);
    }

    if (options?.name && options?.isNameUpdate) {
      updateChildWorksheetSpaceType(
        {
          spaceTypeId: row.spaceTypeId,
          id: row.spaceType.id,
          name:
            clientFacingNamesAreVisible && row.name !== DEFAULT_INDIVIDUAL_NAME
              ? row.name
              : options.name,
          clientFacingName: clientFacingNamesAreVisible
            ? options.name
            : row.projectSpaceType.clientFacingName,
        },
        null
      );
      return;
    }
    populateWorksheetFromRequest(
      Worksheet.toAPIWorksheet({ ...worksheet, rows })
    );
    saveWorksheet();
  }

  // scroll to a newly created row
  scrollToNewRow() {
    setTimeout(() => {
      if (this.newRowRef.current) {
        this.newRowRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
        this.setState({ newRowId: null });
      }
    }, 200);
  }

  renderSubcategoryRows(
    spaceTypeCategory: IProjectSpaceTypeCategory,
    subcategory: IProjectSpaceTypeSubcategory
  ) {
    const { isWorksheetOpenForDownload } = this.props;

    return (
      <SubcategoryRows
        key={`subcategory-${subcategory.id}`}
        category={spaceTypeCategory}
        subcategory={subcategory}
        isWorksheetOpenForDownload={isWorksheetOpenForDownload}
      />
    );
  }

  renderRows(
    rows: WorksheetRow[],
    isWorksheetOutdated: boolean,
    seats: {
      totalWorkSeats: number;
      storedWorkSeats?: number;
      totalCategorySeats: number;
    },
    placeholder?: {
      placeHolderPosition: number;
      dndPlaceholder: JSX.Element;
    }
  ) {
    const {
      worksheet,
      scenario,
      projectSettings,
      downloadParams,
      isShowingWorksheetUpdateDialog,
      isRenderingForExport,
      currentUserIsViewOnly,
      clientFacingNamesAreVisible,
      decimalsAreEnabled,
    } = this.props;

    const {
      collapsed,
      activeRowIdentifier,
      didSetInitialCollapsedState,
      newRowId,
      userIsEditingASpaceName,
    } = this.state;

    // Prevent rendering twice if initialCollapsedState is not set
    if (!didSetInitialCollapsedState) {
      return null;
    }

    const { totalWorkSeats, totalCategorySeats } = seats;

    return rows?.map((row: WorksheetRow, index: number) => {
      const isNotDraggable =
        isIWCategoryOrSubcategory(row.projectSpaceTypeCategory) ||
        row.isStorageSupportSpace ||
        !row?.spaceType.spaceType ||
        isRenderingForExport ||
        isShowingWorksheetUpdateDialog ||
        isTotalProgram(scenario);

      const shouldDisableRows =
        scenario.calculatorType === TOTAL_PROGRAM_TYPE ||
        isShowingWorksheetUpdateDialog ||
        currentUserIsViewOnly;

      const shouldShowChildRow =
        !collapsed[row?.spaceType?.parent?.id] &&
        (scenario.isDetailed || row.isStorageSupportSpace);
      let componentRow: ReactElement;

      const hasChildDepartmentRows = !!worksheet.breakDownRows[row.uniqueKey];

      if (!row.isStorageSupportSpace) {
        const isParentRow =
          !row.child &&
          scenario.isDetailed &&
          isIWCategoryOrSubcategory(row.projectSpaceTypeCategory);

        if (isParentRow || hasChildDepartmentRows) {
          componentRow = (
            <ParentSpaceTypeRow
              belongsToTotalProgram={isTotalProgram(scenario)}
              showDecimals={decimalsAreEnabled}
              disabled={shouldDisableRows}
              active={
                activeRowIdentifier === `${row.spaceType.id}-${!!row.child}`
              }
              name={row.spaceType?.name}
              row={row}
              spaceTypeTotals={worksheet.spaceTypesTotals[row.uniqueKey]}
              totalCategorySeats={totalCategorySeats}
              collapsed={collapsed[this.getCollapsedId(row)]}
              setCollapsed={() =>
                this.toggleParentRowCollapsedState(row, {
                  isTotalProgramParentRow: hasChildDepartmentRows,
                })
              }
              renameSpaceType={this.renameSpaceType}
              shouldDisplayRowValues={hasChildDepartmentRows}
              isPerDepartmentParentRow={hasChildDepartmentRows}
              clientFacingNamesAreVisible={clientFacingNamesAreVisible}
              isDraggable={!isNotDraggable}
            />
          );
          if (hasChildDepartmentRows && !collapsed[this.getCollapsedId(row)]) {
            componentRow = (
              <>
                {componentRow}
                {worksheet.breakDownRows[row.uniqueKey].map((r) => (
                  <SpaceTypeRow
                    key={`${r.id}-${row.uniqueKey}-${r.title}-${r.subtitle}`}
                    row={r}
                    totalCategorySeats={totalCategorySeats}
                    isPerDepartmentRow
                    isDraggable={!isNotDraggable}
                  />
                ))}
              </>
            );
          }

          if (
            !shouldShowChildRow &&
            scenario.isDetailed &&
            hasChildDepartmentRows
          )
            componentRow = null;
        } else if (shouldShowChildRow || !scenario.isDetailed) {
          componentRow = (
            <SpaceTypeRow
              key={row.id}
              row={row}
              totalCategorySeats={totalCategorySeats}
              isDraggable={!isNotDraggable}
            />
          );
        }
      } else if (row.child && shouldShowChildRow && !hasChildDepartmentRows) {
        componentRow = (
          <StorageSpaceTypeRow
            row={row as WorksheetStorageSpaceRow}
            disabled={shouldDisableRows}
            active={
              activeRowIdentifier ===
              `${row?.projectSpaceType?.id}${!!row.child}`
            }
            changeChildRow={this.changeChildRow}
            totalWorkSeats={totalWorkSeats}
            showDecimals={decimalsAreEnabled}
          />
        );
      } else if (!row.child || hasChildDepartmentRows) {
        componentRow = (
          <ParentStorageSpaceTypeRow
            showDecimals={decimalsAreEnabled}
            active={
              activeRowIdentifier === `${row.spaceType.id}-${!!row.child}`
            }
            name={row.spaceType?.name}
            row={row as WorksheetStorageSpaceRow}
            spaceTypeTotals={worksheet.spaceTypesTotals[row.uniqueKey]}
            collapsed={collapsed[this.getCollapsedId(row)]}
            setCollapsed={() =>
              this.toggleParentRowCollapsedState(row, {
                isTotalProgramParentRow: hasChildDepartmentRows,
              })
            }
            detailedIndividualConfig={scenario.isDetailed}
            shouldDisplayRowValues={hasChildDepartmentRows}
            isPerDepartmentParentRow={hasChildDepartmentRows}
          />
        );
        if (hasChildDepartmentRows && !collapsed[this.getCollapsedId(row)]) {
          componentRow = (
            <>
              {componentRow}
              {worksheet.breakDownRows[row.uniqueKey].map((r) => (
                <StorageSpaceTypeRow
                  key={`${r.id}-${row.uniqueKey}-${r.title}-${r.subtitle}`}
                  row={r as WorksheetStorageSpaceRow}
                  disabled={shouldDisableRows}
                  totalWorkSeats={totalWorkSeats}
                  showDecimals={decimalsAreEnabled}
                  isPerDepartmentRow
                />
              ))}
            </>
          );
        }
        if (!shouldShowChildRow && hasChildDepartmentRows) componentRow = null;
      }

      if (row.isStorageSupportSpace) {
        if (isTotalProgram(scenario) && !row.child) {
          const relatedChildren = worksheet.rows.filter(
            (r) => r.child && r?.spaceType?.parent?.id === row?.spaceType?.id
          );

          if (!relatedChildren.length) return null;
        }

        if (!row.child) {
          componentRow = (
            <div key={row.id}>
              <StorageCategoryHeader
                spaceType={row.spaceType.spaceType as string}
                projectSettings={projectSettings}
                key="storage-cat-header"
                isDownload={isRenderingForExport}
              />
              {componentRow}
            </div>
          );
        }
      }

      return (
        <div
          key={row.uniqueKey}
          ref={
            `${row.projectSpaceType.id}-${!!row.child}` === newRowId
              ? this.newRowRef
              : null
          }
          style={{
            backgroundColor: downloadParams?.category ? 'white' : '',
            // 80px for the worksheet header
            // then, we wanted to give a little more context to the user about where they are to avoid confusion.
            // 120px is the category header (80) + gray subheader (40)
            scrollMarginTop: '200px',
          }}
        >
          {isNotDraggable ? (
            componentRow
          ) : (
            <Draggable
              draggableId={`${row.projectSpaceType?.id}`}
              index={index}
              key={`${row.projectSpaceType?.id}-${!!row.child}-${
                row.category
              }-dragger`}
              isDragDisabled={userIsEditingASpaceName || currentUserIsViewOnly}
            >
              {(provided, snapshot) => (
                <div
                  role="button"
                  tabIndex={0}
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                  onMouseDown={this.handleRowMouseDown}
                  onKeyDown={this.handleRowKeyDown}
                  style={{
                    ...getItemStyle(
                      snapshot.isDragging,
                      provided.draggableProps.style
                    ),
                    outline: 'none',
                    outlineColor: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  {componentRow}
                </div>
              )}
            </Draggable>
          )}
          {placeholder &&
            placeholder.placeHolderPosition === index &&
            placeholder.dndPlaceholder}
        </div>
      );
    });
  }

  renderCategoryRows(
    spaceTypeCategory: IProjectSpaceTypeCategory,
    placeholder?: JSX.Element
  ) {
    const {
      worksheet,
      isLoading,
      scenario,
      user,
      isWorksheetOpenForDownload,
      isShowingWorksheetUpdateDialog,
      isRenderingForExport,
      currentUserIsViewOnly,
      projectColors,
      decimalsAreEnabled,
    } = this.props;
    if (!worksheet) return null;
    if (!scenario) return null;
    if (isLoading) {
      return (
        <CenteredColumn>
          <CircularProgress />
        </CenteredColumn>
      );
    }

    const isDownload = isRenderingForExport;
    const { rowsByCategory } = worksheet;
    const categoryRows =
      rowsByCategory[spaceTypeCategory.defaultSpaceTypeCategory.category] || [];

    const isMacro = scenario.calculatorType === MACRO_PROGRAMMING_TYPE;

    /*
      react-dnd has a 'placeholder' element that is inserted in the list to fix
      styling oddities when dragging another object (and allows for an 'end' index)
      to be selected and dragged to.
      we need to put this placeholder element at the end of every category of spaces, except for support spaces
      where it goes directly before storage support spaces
    */
    const placeHolderPosition =
      spaceTypeCategory.defaultSpaceTypeCategory.category ===
      SUPPORT_SPACES_CATEGORY
        ? _.filter(categoryRows, (r) => !r.isStorageSupportSpace).length - 1
        : categoryRows.length - 1;

    const totalWorkSeats = scenario.workSeats;
    const { category } = spaceTypeCategory.defaultSpaceTypeCategory;
    const { seats: totalCategorySeats, USF: totalCategoryUSF } =
      worksheet.categoriesTotals[category] || {};

    const sortedRows = categoryRows.slice();

    const { subcategories, customSubcategories } = (
      spaceTypeCategory.subcategories?.filter(
        (subcategory: IProjectSpaceTypeSubcategory) => subcategory.name
      ) || []
    ).reduce(
      (groupedSubcategories, subcategory: IProjectSpaceTypeSubcategory) => {
        if (
          subcategory.defaultSpaceTypeCategory.category !== CUSTOM_SUBCATEGORY
        ) {
          return {
            ...groupedSubcategories,
            subcategories: [...groupedSubcategories.subcategories, subcategory],
          };
        }

        return {
          ...groupedSubcategories,
          customSubcategories: {
            ...groupedSubcategories.customSubcategories,
            includedInWorkSeats: subcategory.includeInTotalWorkSeats
              ? [
                  ...groupedSubcategories.customSubcategories
                    .includedInWorkSeats,
                  subcategory,
                ]
              : groupedSubcategories.customSubcategories.includedInWorkSeats,
            notIncludedInWorkSeats: !subcategory.includeInTotalWorkSeats
              ? [
                  ...groupedSubcategories.customSubcategories
                    .notIncludedInWorkSeats,
                  subcategory,
                ]
              : groupedSubcategories.customSubcategories.notIncludedInWorkSeats,
          },
        };
      },
      {
        subcategories: [],
        customSubcategories: {
          includedInWorkSeats: [],
          notIncludedInWorkSeats: [],
        },
      }
    );

    const { categories } = worksheet;
    const worksheetCategories = isTotalProgram(scenario)
      ? (subcategories || [])
          .concat(customSubcategories || [])
          .map((subcategory) => ({
            categoryId: subcategory.id,
            visible: true,
          }))
      : categories;

    const worksheetCategoriesById: Record<number, WorksheetCategory> = (
      worksheetCategories || []
    ).reduce((map, worksheetCategory) => {
      return {
        ...map,
        [worksheetCategory.categoryId]: worksheetCategory,
      };
    }, {});

    return (
      <div key={spaceTypeCategory.id}>
        <SpaceTypeCategoryRow
          scenario={scenario}
          user={user}
          worksheet={worksheet}
          usf={totalCategoryUSF || 0}
          totalWorksheetUsf={worksheet.subtotalProgramUSF}
          color={getColor(
            spaceTypeCategory.defaultSpaceTypeCategory.category,
            projectColors
          )}
          totalWorkSeats={
            spaceTypeCategory.defaultSpaceTypeCategory.category ===
            INDIVIDUAL_WORKSPACES_CATEGORY
              ? totalWorkSeats
              : totalCategorySeats || 0
          }
          onManageSpaceTypesButtonClick={this.onManageSpaceTypesButtonClick}
          projectSpaceTypeCategory={spaceTypeCategory}
          showDecimals={decimalsAreEnabled}
          propRef={
            this.refMap[spaceTypeCategory.defaultSpaceTypeCategory.category]
          }
          isDownload={isDownload}
          isInitiallyDisabled={
            isShowingWorksheetUpdateDialog || currentUserIsViewOnly
          }
        />
        {subcategories
          .concat(customSubcategories.includedInWorkSeats)
          .map((subcategory: IProjectSpaceTypeSubcategory) => {
            return worksheetCategoriesById[subcategory.id]?.visible ? (
              <div key={`subcategory-${subcategory.id}`}>
                {this.renderSubcategoryRows(spaceTypeCategory, subcategory)}
              </div>
            ) : null;
          })}
        {/* We show the row totaling the values in individual/private offices. */}
        {spaceTypeCategory.defaultSpaceTypeCategory.category ===
          INDIVIDUAL_WORKSPACES_CATEGORY && (
          <WorkSeatSubTotalRow
            worksheetWorkSeats={worksheet.workspacesSeats}
            totalWorkSeats={totalWorkSeats}
            showDecimals={decimalsAreEnabled}
            isDownload={isDownload}
          />
        )}
        {customSubcategories.notIncludedInWorkSeats.map(
          (subcategory: IProjectSpaceTypeSubcategory) => {
            return worksheetCategoriesById[subcategory.id]?.visible ? (
              <div key={`subcategory-${subcategory.id}`}>
                {this.renderSubcategoryRows(spaceTypeCategory, subcategory)}
              </div>
            ) : null;
          }
        )}
        {!isIWCategoryOrSubcategory(spaceTypeCategory) &&
          this.showHeader(sortedRows) &&
          (isMacro ? (
            <MacroProgrammingCategoryHeader
              name={spaceTypeCategory.name}
              categoryOrSubcategory={spaceTypeCategory}
              isWorksheetOpenForDownload={isWorksheetOpenForDownload}
              showDetailedSharedLabels={!scenario.isShared}
              isRatio={shouldDisplayAsRatio(spaceTypeCategory)}
            />
          ) : (
            <ProgrammingCategoryHeader
              isRatio={shouldDisplayAsRatio(spaceTypeCategory)}
              name={spaceTypeCategory.name}
              categoryOrSubcategory={spaceTypeCategory}
            />
          ))}
        {!spaceTypeCategory.subcategories?.length &&
          this.renderRows(
            sortedRows,
            scenario.areStoredWorkSeatsOutdated,
            {
              totalWorkSeats: scenario.workSeats,
              storedWorkSeats: scenario.storedWorkSeats,
              totalCategorySeats,
            },
            {
              placeHolderPosition,
              dndPlaceholder: placeholder,
            }
          )}
      </div>
    );
  }

  renderNotesIfDownloading(
    shouldShowNotes: boolean,
    generalNotes: ICategoryNote[],
    notesByCategory: { [index: string]: ICategoryNote[] },
    scenarioDepartment: IDepartment,
    currentFloor: IFloor
  ) {
    const {
      scenario,
      downloadParams = {},
      tpsWorksheetsEnhanced,
      projectColors,
    } = this.props;

    if (
      isTotalProgram(scenario) &&
      shouldShowNotes &&
      (!downloadParams?.category ||
        downloadParams?.category === INDIVIDUAL_WORKSPACES_CATEGORY)
    ) {
      return (
        <TotalProgramWorksheetNotes
          totalProgram={scenario}
          category={{
            name: GENERAL_CATEGORY_TITLE,
            key: NOTE_GENERAL_CATEGORY,
          }}
          scenarioWorksheets={getWorksheetsByScenarioIdMap(
            tpsWorksheetsEnhanced,
            true
          )}
          projectColors={projectColors}
          isDownload
        />
      );
    }

    if (
      generalNotes?.length > 0 &&
      (downloadParams?.category === INDIVIDUAL_WORKSPACES_CATEGORY ||
        downloadParams?.category === undefined)
      // undefined indicates all categories are downloaded, including INDIVIDUAL_WORKSPACES_CATEGORY
    ) {
      return (
        <WorksheetNote
          notesByCategory={notesByCategory}
          category={{
            name: GENERAL_CATEGORY_TITLE,
            key: NOTE_GENERAL_CATEGORY,
          }}
          scenario={scenario}
          projectColors={projectColors}
          department={scenarioDepartment}
          floor={currentFloor}
          isDownload
          options={{
            showCategoryName: true,
            showBorder: true,
          }}
        />
      );
    }

    return null;
  }

  render() {
    const {
      worksheet,
      isEditingSavedScenario,
      tpsWorksheetsEnhanced,
      projectColors,
      showWorksheetNotesDialog,
      downloadParams = {},
      building,
      project,
      inBrowserDownload,
      saveWorksheet,
      programSummaryCalculator,
      scenario,
      isShowingWorksheetUpdateDialog,
      isWorksheetOpenForDownload,
      isRenderingForExport,
      location,
      currentUserIsViewOnly,
      clientFacingNamesAreVisible,
      toggleClientFacingNames,
      decimalsAreEnabled,
    } = this.props;

    const urlParams = new URLSearchParams(location?.search);

    const shouldShowNotes =
      isRenderingForExport && urlParams.get('show_notes') === 'true';

    const {
      activeRowIdentifier,
      manageSpaceTypesDialogAnchorEl,
      collapsed,
      userIsEditingASpaceName,
      selectedSpaceTypeCategoryId,
      newRowId,
    } = this.state;

    if (!worksheet || !worksheet.rows) return null;
    if (isEditingSavedScenario && !isShowingWorksheetUpdateDialog) return null;

    const isDownload = isRenderingForExport;
    let { spaceTypeCategories } = project;

    if (isDownload) {
      const isMacro = scenario.calculatorType === MACRO_PROGRAMMING_TYPE;
      const sharedNotesCategory = snakeCase(
        SHARED_SPACES_CATEGORY
      ).toUpperCase();

      const sharedNotes =
        isMacro && scenario.isShared
          ? worksheet.notes?.filter(
              (note: ICategoryNote) =>
                note.note && note.category === sharedNotesCategory
            )
          : [];
      const notesByCategory = groupBy((note: ICategoryNote) => note.category)(
        worksheet.notes || []
      );

      const currentFloor = building?.floors?.find(
        (floor: IFloor) => floor.id === scenario.floorId
      );

      const generalNotes = notesByCategory[
        NOTE_GENERAL_CATEGORY.toUpperCase()
      ]?.filter((note: ICategoryNote) => note.note);

      spaceTypeCategories = downloadParams?.category
        ? spaceTypeCategories.filter(
            (spaceTypeCategory: IProjectSpaceTypeCategory) =>
              spaceTypeCategory.defaultSpaceTypeCategory.category ===
              downloadParams.category
          )
        : spaceTypeCategories;

      return (
        <WorksheetContext.Provider
          value={{
            clientFacingNamesAreVisible,
            userIsEditingASpaceName,
            isRenderingForExport,
            isWorksheetOpenForDownload,
            showDecimals: decimalsAreEnabled,
            activeRowIdentifier,
            changeRow: this.onChangeRow,
            changeChildRow: this.changeChildRow,
            onRowNameFocus: () =>
              this.setState({ userIsEditingASpaceName: true }),
            onRowNameBlur: () =>
              this.setState({ userIsEditingASpaceName: false }),
            renameSpaceType: this.renameSpaceType,
            collapsed,
            setCollapsedRow: this.setCollapsedRow,
            newRowRef: this.newRowRef,
            newRowId,
          }}
        >
          <div style={{ backgroundColor: 'white' }}>
            <Grid container key="download header">
              <Grid item xs={12}>
                <ScenarioHeader
                  scenario={scenario}
                  building={building}
                  styles={{ marginBottom: 0, paddingLeft: 8 }}
                />
              </Grid>
            </Grid>
            <WorksheetHeader
              key="header"
              worksheet={worksheet}
              programSummaryCalculator={programSummaryCalculator}
              scenario={scenario}
              projectColors={projectColors}
              project={project}
              toggleShowDecimals={this.toggleShowDecimals}
              showDecimals={decimalsAreEnabled}
              shouldHideDecimalsSwitch={isRenderingForExport}
              toggleClientFacingNamesVisibility={toggleClientFacingNames}
              clientFacingNamesAreVisible={clientFacingNamesAreVisible}
            />
            {shouldShowNotes &&
              this.renderNotesIfDownloading(
                shouldShowNotes,
                generalNotes,
                notesByCategory,
                scenario.department,
                currentFloor
              )}
            {spaceTypeCategories.map(
              (spaceTypeCategory: IProjectSpaceTypeCategory) => {
                const notesCategory = snakeCase(
                  spaceTypeCategory.defaultSpaceTypeCategory.category
                ).toUpperCase();

                const notes = notesByCategory[notesCategory]?.filter(
                  (categoryNote: ICategoryNote) => categoryNote.note
                );

                const worksheetNotes =
                  isTotalProgram(scenario) &&
                  (inBrowserDownload || shouldShowNotes) ? (
                    <TotalProgramWorksheetNotes
                      totalProgram={scenario}
                      category={{
                        name: spaceTypeCategory.name,
                        key:
                          spaceTypeCategory.defaultSpaceTypeCategory.category,
                      }}
                      scenarioWorksheets={getWorksheetsByScenarioIdMap(
                        tpsWorksheetsEnhanced,
                        true
                      )}
                      projectColors={projectColors}
                      isDownload
                      key={`total-program-notes-${notesCategory}`}
                    />
                  ) : (
                    notes?.length > 0 &&
                    shouldShowNotes && (
                      <WorksheetNote
                        notesByCategory={notesByCategory}
                        category={{
                          name: spaceTypeCategory.name,
                          key:
                            spaceTypeCategory.defaultSpaceTypeCategory.category,
                        }}
                        scenario={scenario}
                        projectColors={projectColors}
                        department={scenario.department}
                        floor={currentFloor}
                        isDownload
                        options={{
                          showCategoryName: true,
                          showBorder: true,
                        }}
                        key={`notes-${notesCategory}`}
                      />
                    )
                  );

                return isIWCategoryOrSubcategory(spaceTypeCategory) ? (
                  <div key={`subcategory-${spaceTypeCategory.id}`}>
                    {scenario.isShared && (
                      <>
                        <SharedSpacesRow
                          projectColors={projectColors}
                          disabled={
                            isShowingWorksheetUpdateDialog ||
                            decimalsAreEnabled ||
                            currentUserIsViewOnly
                          }
                          worksheet={worksheet}
                          showDecimals={decimalsAreEnabled}
                          updateWorksheet={(w) => {
                            const { populateWorksheet } = this.props;
                            populateWorksheet(w);
                            saveWorksheet();
                          }}
                          propRef={null}
                        />
                        {isMacro && sharedNotes?.length > 0 && shouldShowNotes && (
                          <WorksheetNote
                            notesByCategory={{
                              [sharedNotesCategory]: sharedNotes,
                            }}
                            category={{
                              name: TITLES.SHARED_SPACES,
                              key: SHARED_SPACES_CATEGORY,
                            }}
                            scenario={scenario}
                            projectColors={projectColors}
                            isDownload
                            options={{
                              showCategoryName: true,
                              showBorder: true,
                            }}
                          />
                        )}
                      </>
                    )}
                    {this.renderCategoryRows(spaceTypeCategory)}
                    {worksheetNotes}
                  </div>
                ) : (
                  !scenario.isShared && (
                    <div key={`rows-${spaceTypeCategory.id}`}>
                      {this.renderCategoryRows(spaceTypeCategory)}
                      {worksheetNotes}
                    </div>
                  )
                );
              }
            )}
          </div>
        </WorksheetContext.Provider>
      );
    }

    return (
      <WorksheetContainer
        optionsAreDisabled={scenario?.areStoredWorkSeatsOutdated}
        clientFacingNamesAreVisible={clientFacingNamesAreVisible}
        showDecimals={decimalsAreEnabled}
      >
        <WorksheetContext.Provider
          value={{
            clientFacingNamesAreVisible,
            userIsEditingASpaceName,
            isRenderingForExport,
            isWorksheetOpenForDownload,
            showDecimals: decimalsAreEnabled,
            activeRowIdentifier,
            changeRow: this.onChangeRow,
            changeChildRow: this.changeChildRow,
            onRowNameFocus: () =>
              this.setState({ userIsEditingASpaceName: true }),
            onRowNameBlur: () =>
              this.setState({ userIsEditingASpaceName: false }),
            renameSpaceType: this.renameSpaceType,
            collapsed,
            setCollapsedRow: this.setCollapsedRow,
            newRowRef: this.newRowRef,
            newRowId,
          }}
        >
          {showWorksheetNotesDialog && <WorksheetNotes />}
          <WorksheetHeader
            worksheet={worksheet}
            key="header"
            programSummaryCalculator={programSummaryCalculator}
            scenario={scenario}
            projectColors={projectColors}
            project={project}
            toggleShowDecimals={this.toggleShowDecimals}
            clientFacingNamesAreVisible={clientFacingNamesAreVisible}
            toggleClientFacingNamesVisibility={toggleClientFacingNames}
            showDecimals={decimalsAreEnabled}
            scrollTo={this.scrollToCategory}
          />
          {/* In order to allow sorting only within category we split all rows into each relevant category */}
          {spaceTypeCategories?.map(
            (spaceTypeCategory: IProjectSpaceTypeCategory) =>
              isIWCategoryOrSubcategory(spaceTypeCategory) ? (
                <div key={`subcategory-${spaceTypeCategory.id}`}>
                  {scenario.isShared && (
                    <SharedSpacesRow
                      projectColors={projectColors}
                      disabled={
                        isShowingWorksheetUpdateDialog || currentUserIsViewOnly
                      }
                      worksheet={worksheet}
                      showDecimals={decimalsAreEnabled}
                      updateWorksheet={(w) => {
                        const { populateWorksheet } = this.props;
                        populateWorksheet(w);
                        saveWorksheet();
                      }}
                      propRef={this.refMap[SHARED_SPACES_CATEGORY]}
                    />
                  )}
                  {this.renderCategoryRows(spaceTypeCategory)}
                </div>
              ) : (
                !scenario.isShared && (
                  <DragDropContext
                    onDragEnd={(drop) =>
                      this.handleDragEnd(drop, spaceTypeCategory)
                    }
                    key={spaceTypeCategory.id}
                  >
                    <StrictModeDroppable
                      droppableId={spaceTypeCategory.id.toString()}
                      isDropDisabled={
                        currentUserIsViewOnly || isShowingWorksheetUpdateDialog
                      }
                    >
                      {(droppableProvided, droppableSnapshot) => (
                        <div
                          {...droppableProvided.droppableProps}
                          ref={droppableProvided.innerRef}
                          style={getListStyle(droppableSnapshot.isDraggingOver)}
                          key={
                            droppableProvided.droppableProps[
                              'data-rbd-droppable-id'
                            ]
                          }
                        >
                          {this.renderCategoryRows(
                            spaceTypeCategory,
                            droppableProvided.placeholder
                          )}
                        </div>
                      )}
                    </StrictModeDroppable>
                  </DragDropContext>
                )
              )
          )}
          {manageSpaceTypesDialogAnchorEl && (
            <ManageSpaceTypesPopover
              selectedCategoryId={selectedSpaceTypeCategoryId}
              anchorEl={manageSpaceTypesDialogAnchorEl}
              onClose={this.onCloseManageSpaceTypesDialog}
            />
          )}
        </WorksheetContext.Provider>
      </WorksheetContainer>
    );
  }
}

const mapStateToProps = (app: IAppState) => ({
  allCategories: app.projectsReducer.spaceTypeCategories,
  projectSpaceTypes: app.spaceTypesReducer.spaceTypes,
  childWorksheetSpaceTypes: app.calculatorReducer.childWorksheetSpaces,
  scenario: app.calculatorReducer.currentScenario,
  programSummaryCalculator: selectProgramSummaryCalculator(app),
  worksheet: app.calculatorReducer.worksheet,
  tpsWorksheetsEnhanced: selectTpsWorksheets(app),
  isLoading: app.calculatorReducer.isLoading,
  building: pathOr(
    null,
    ['projectsReducer', 'currentProject', 'buildings', 0],
    app
  ),
  user: app.userReducer.user,
  isEditingSavedScenario: app.calculatorReducer.isEditingSavedScenario,
  projectColors: app.projectsReducer.colors,
  showWorksheetNotesDialog: app.calculatorReducer.showWorksheetNotesDialog,
  project: app.projectsReducer.currentProject,
  projectSettings: app.settingsReducer.projectSettings,
  canDeleteSpaceType: app.worksheetReducer.canDeleteSpaceType,
  isShowingWorksheetUpdateDialog:
    app.calculatorReducer.showWorksheetUpdateDialog,
  currentUserIsViewOnly: selectCurrentUserIsViewOnly(app),
  decimalsAreEnabled: selectWorksheetDecimalsAreEnabled(app),
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      getSpaceTypeCategories: getSpaceTypeCategoriesAction,
      setIsLoading: setIsLoadingAction,
      saveWorksheet: saveWorksheetAction,
      fetchCurrentWorksheet: fetchCurrentWorksheetAction,
      populateWorksheet: populateWorksheetAction,
      setCurrentParent: customSpaceActions.setCurrentParent,
      postWorksheetRow: postWorksheetRowAction,
      clearSpaceCategories: clearSpaceCategoriesAction,
      createCustomSpaceType: customSpaceActions.createCustomSpaceType,
      updateCustomSpaceType: customSpaceActions.updateCustomSpaceType,
      updateProjectSpaceType: updateProjectSpaceTypeAction,
      updateChildWorksheetSpaceType: updateChildWorksheetSpaceTypeAction,
      bulkUpdateProjectSpaceTypes:
        spaceTypesActions.bulkUpdateProjectSpaceTypes,
      setIsSpaceTypeDeletable: setIsSpaceTypeDeletableAction,
      fetchScenariosAssociatedWithSelectedRow: fetchScenariosAssociatedWithSelectedRowAction,
      setSelectedWorksheetRow: setSelectedWorksheetRowAction,
      setSelectedSpaceType: setSelectedSpaceTypeAction,
      setSelectedChildSpaceType: setSelectedChildSpaceTypeAction,
      setRemoveSpaceTypeDialogStatus: setRemoveSpaceTypeDialogStatusAction,
      populateWorksheetFromRequest: populateWorksheetFromRequestAction,
      toggleClientFacingNames: toggleClientFacingNamesAreVisible,
      setWorksheetDecimalsAreEnabled: setWorksheetDecimalsAreEnabledAction,
    },
    dispatch
  );

const ProgramingWorksheet = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(Programming)
);

export default ProgramingWorksheet;
